import { RebuyableMechanicState } from "./game-mechanics";

export const shop = {};

export const ShopPurchaseData = {
  totalSTD: 0,
  spentSTD: 0,
  unlockedCosmetics: [],

  get availableSTD() {
    return this.totalSTD - this.spentSTD;
  },

  get isIAPEnabled() {
    return Cloud.loggedIn && this.availableSTD >= 0 && player.IAP.enabled;
  },


  getClearSTD: () => {
    const emptyData = {};
    emptyData.totalSTD = 0;
    emptyData.spentSTD = 0;
    emptyData.unlockedCosmetics = [];
    for (const key of Object.keys(GameDatabase.shopPurchases)) emptyData[key] = 0;
    return emptyData;
  },

  // Combines std data to an object and returns it
  getLocalSTD() {
    const localSTD = {};
    localSTD.totalSTD = this.totalSTD;
    localSTD.spentSTD = this.spentSTD;
    localSTD.unlockedCosmetics = this.unlockedCosmetics;
    for (const key of Object.keys(GameDatabase.shopPurchases)) localSTD[key] = this[key] ?? 0;
    return localSTD;
  },

  // Does not return anything, applies inputted std data and saves the game
  setLocalSTD(newData) {
    this.totalSTD = newData.totalSTD;
    this.spentSTD = newData.spentSTD;
    this.unlockedCosmetics = [...(newData.unlockedCosmetics ?? [])];
    for (const key of Object.keys(GameDatabase.shopPurchases)) this[key] = newData[key] ?? 0;
    if (this.allCosmeticSets > 0) this.unlockedCosmetics = Object.keys(GameDatabase.reality.glyphCosmeticSets);
    if (ShopPurchaseData.isIAPEnabled) Speedrun.setSTDUse(true);
  },


  // Fetches std data from the claud and returns it as an object
  async getRemoteSTD() {
    let newData;
    await playerYG.getData(["std"]).then(_data => {
      if (Object.keys(_data).length === 0) {
        newData = ShopPurchaseData.getClearSTD();
      } else {
        newData = JSON.parse(_data.std);
      }
    }).catch(() => {
      GameUI.notify.error("Не удалось загрузить покупки ЗППП!", 10000);
    });
    return newData;
  },

  async setRemoteSTD(newData) {
    await playerYG.getData().then(_data => {
      _data.std = JSON.stringify(newData);
      playerYG.setData(_data);
    });
  },

  buyUpgrade(upgradeKey, cosmeticName) {
    const stdData = this.getLocalSTD();
    if (GameDatabase.shopPurchases[upgradeKey].cost > ShopPurchaseData.availableSTD) {
      GameUI.notify.error(`Недостсточно ЗППП коинов для покупки`, 10000);
      return false;
    }
    if (cosmeticName in stdData.unlockedCosmetics) {
      GameUI.notify.error(`У вас уже имеется выбранный набор Глифов`, 10000);
      return false;
    }
    stdData.spentSTD += GameDatabase.shopPurchases[upgradeKey].cost;
    stdData[upgradeKey] += 1;
    if (typeof cosmeticName !== "undefined") {
      stdData.unlockedCosmetics.push(cosmeticName);
    }

    GameUI.notify.info(`Успешно потрачено ${GameDatabase.shopPurchases[upgradeKey].cost} ЗППП коинов`, 10000);
    ShopPurchaseData.setLocalSTD(stdData);
    ShopPurchaseData.setRemoteSTD(stdData);
    return true;
  },
};

// We track the local state of shop purchases here, so dynamically add all the keys which exist in the gameDB
for (const key of Object.keys(GameDatabase.shopPurchases)) ShopPurchaseData[key] = 0;

class ShopPurchaseState extends RebuyableMechanicState {
  get currency() {
    return ShopPurchaseData.availableSTD;
  }

  get isAffordable() {
    return this.currency >= this.cost;
  }

  get description() {
    const desc = this.config.description;
    return typeof desc === "function" ? desc() : desc;
  }

  get cost() {
    const cost = this.config.cost;
    return typeof cost === "function" ? cost() : cost;
  }

  // ShopPurchaseData for any particular key is undefined in between page load and STD load,
  // so we need to guard against that causing NaNs to propagate through the save
  get purchases() {
    return ShopPurchaseData[this.config.key] ?? 0;
  }

  set purchases(value) {
    if (!Number.isFinite(value)) return;
    ShopPurchaseData[this.config.key] = value;
  }

  isUnlocked() {
    return player.records.fullGameCompletions > 0 || (this.config.isUnlocked?.() ?? true);
  }

  get lockText() {
    return this.config.lockText;
  }

  get shouldDisplayMult() {
    return Boolean(this.config.multiplier);
  }

  get currentMult() {
    if (!this.shouldDisplayMult) return "";
    return this.config.multiplier(ShopPurchaseData.isIAPEnabled ? this.purchases : 0);
  }

  get nextMult() {
    if (!this.shouldDisplayMult) return "";
    return this.config.multiplier(ShopPurchaseData.isIAPEnabled ? this.purchases + 1 : 0);
  }

  // We want to still display the correct value in the button, so we need separate getters for it
  get currentMultForDisplay() {
    if (!this.shouldDisplayMult) return "";
    return this.config.multiplier(this.purchases);
  }

  get nextMultForDisplay() {
    if (!this.shouldDisplayMult) return "";
    return this.config.multiplier(this.purchases + 1);
  }

  formatEffect(effect) {
    return this.config.formatEffect?.(effect) || formatX(effect, 2, 0);
  }

  purchase() {
    if (!Cloud.loggedIn) {
      Cloud.login();
      return false;
    }
    if (!this.isAffordable) {
      SecretAchievement(33).unlock();
      if (Cloud.loggedIn) Modal.shop.show();
      else Modal.message.show("Вы не можете купить ЗППП-коины без авторизации.");
      return false;
    }
    if (!this.canBeBought) {
      return false;
    }
    if (GameEnd.creditsEverClosed) {
      return false;
    }
    if (this.config.instantPurchase && ui.$viewModel.modal.progressBar) {
      return false;
    }

    const cosmeticId = this.config.key === "singleCosmeticSet"
      ? GlyphAppearanceHandler.chosenFromModal?.id
      : undefined;

    // Contact the purchase provider to verify the purchase
    const result = ShopPurchaseData.buyUpgrade(this.config.key, cosmeticId);

    // IMPORTANT!!!
    if (!result) return false;

    if (player.IAP.enabled) Speedrun.setSTDUse(true);
    if (this.config.instantPurchase) this.config.onPurchase();
    GameUI.update();
    return true;
  }
}

export const ShopPurchase = mapGameDataToObject(
  GameDatabase.shopPurchases,
  config => new ShopPurchaseState(config)
);

shop.purchaseTimeSkip = function() {
  Speedrun.setSTDUse(true);
  simulateTime(3600 * 6);
};

shop.purchaseLongerTimeSkip = function() {
  Speedrun.setSTDUse(true);
  simulateTime(3600 * 24);
};
